import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';
import {
  CheckCircleIcon,
  GraduationCapIcon,
  StarIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { Button } from '../../../Button';
import { Spinner } from '../../../Spinner';
import {
  StyledAnswer,
  StyledAnswers,
  StyledBody,
  StyledCentered,
  StyledFill,
  StyledFooter,
  StyledProgress,
  StyledQuestion,
  StyledScore,
  StyledScoreMessage,
  StyledStar,
  StyledStars,
  StyledStartSubtitle,
  StyledStartTitle,
  StyledState,
  StyledTrack,
  StyledWrapper,
  type AnswerState,
} from './Quiz.styles';
import {
  restoreAttempt,
  sampleQuestions,
  scoreMessageKey,
  toSavedAnswers,
} from './utils';

export interface QuizAddonAnswer {
  text: string;
  isCorrect: boolean;
}

export interface QuizAddonQuestion {
  question: string;
  answers: QuizAddonAnswer[];
}

/** The first answer of an attempt in this visit. */
export interface QuizStartEvent {
  /** True when continuing an attempt restored from an earlier visit. */
  resume: boolean;
}

/**
 * A single answered question. Array fields (`selectedOptionIndices` /
 * `isCorrect`) support both single- and multi-select questions.
 */
export interface QuizAnswerEvent {
  /** Position within this attempt's drawn questions. */
  questionIndex: number;
  /** Position in the full published pool, before the random draw. */
  poolQuestionIndex: number;
  selectedOptionIndices: number[];
  isCorrect: boolean[];
}

/** A finished quiz attempt, emitted when its last question is answered. */
export interface QuizAttemptEvent {
  answers: QuizAnswerEvent[];
  /** Percentage correct, 0–100. */
  score: number;
  totalCorrect: number;
}

/**
 * Quiz interaction callbacks, grouped into one prop. Each is optional so the
 * quiz runs standalone (Storybook, tests). The app wires these to analytics.
 */
export interface QuizAnalyticEvents {
  /** Fired on the first answer of an attempt in this visit, new or resumed. */
  onStart?: (start: QuizStartEvent) => void;
  /** Fired each time a question is answered (not for restored answers). */
  onAnswer?: (answer: QuizAnswerEvent) => void;
  /** Fired when the last question is answered (not for a restored attempt). */
  onComplete?: (attempt: QuizAttemptEvent) => void;
}

export interface QuizAddonProps {
  /** The full generated question pool (the parsed quiz asset). */
  questions: QuizAddonQuestion[];
  /**
   * How many of `questions` one attempt serves — the backend
   * `QuizFunctionalSettings.nb_of_questions_per_subset`. Each attempt
   * (including a retry) draws a fresh random subset from the pool; unset, 0,
   * or >= the pool size serves the whole pool in order.
   */
  questionsPerAttempt?: number;
  /** Shows a spinner instead of the quiz while the quiz asset loads. */
  loading?: boolean;
  /** Shows an error state instead of the quiz. */
  error?: boolean;
  className?: string;
  /** Interaction callbacks (start / per-answer / complete). */
  analyticEvents?: QuizAnalyticEvents;
  /**
   * The visitor's saved attempt, read once on mount. Undefined starts fresh.
   * A full attempt opens on the results screen; a partial one resumes on the
   * next unanswered question. Invalid answers (outside the pool) are ignored.
   */
  savedAnswers?: QuizSavedAnswer[];
  /**
   * Called with the whole current attempt after each answer (`[]` on retry);
   * `finished` is true when the visitor finishes the attempt.
   */
  onProgressChange?: (answers: QuizSavedAnswer[], finished?: boolean) => void;
}

/** One answered question of an attempt, by pool position. */
export interface QuizSavedAnswer {
  /** Position in the full published pool. */
  poolIndex: number;
  /** Position of the chosen option within the question. */
  answerIndex: number;
}

type Phase = 'start' | 'active' | 'done';

/** Trailing icon for an answered option (correct/revealed = check, wrong = cross). */
const answerIcon = (state: AnswerState) => {
  if (state === 'correct' || state === 'reveal')
    return <CheckCircleIcon size={18} weight="fill" />;
  if (state === 'wrong') return <XCircleIcon size={18} weight="fill" />;
  return null;
};

/**
 * Quiz — Knowledge Hub add-on that runs a multiple-choice quiz one question at
 * a time: an intro screen, then per-question selection that reveals the correct
 * answer (and flags a wrong pick), then a results screen with the score, a star
 * rating, and a retry. Stateless re: data — the app supplies `questions` (the
 * parsed quiz); this component owns the interaction state and its own strings
 * (keys under `experienceV2.quiz.*`, registered in the consuming apps' locales).
 */
export const Quiz = ({
  loading,
  error,
  className,
  ...attemptProps
}: QuizAddonProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  if (loading)
    return (
      <StyledState className={className}>
        <Spinner />
      </StyledState>
    );
  if (error)
    return (
      <StyledState className={className}>
        {t('experienceV2.quiz.error')}
      </StyledState>
    );
  if (attemptProps.questions.length === 0)
    return (
      <StyledState className={className}>
        {t('experienceV2.quiz.empty')}
      </StyledState>
    );

  // The attempt restores from `savedAnswers` on its first mount, so it only
  // mounts once the loading state above is over.
  return <QuizAttempt className={className} {...attemptProps} />;
};

type QuizAttemptProps = Omit<QuizAddonProps, 'loading' | 'error'>;

const QuizAttempt = ({
  questions: pool,
  questionsPerAttempt,
  className,
  analyticEvents,
  savedAnswers,
  onProgressChange,
}: QuizAttemptProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  // The saved attempt, rebuilt once on mount.
  const [restored] = useState(() =>
    restoreAttempt(pool, questionsPerAttempt, savedAnswers),
  );
  const [phase, setPhase] = useState<Phase>(() => {
    if (!restored) return 'start';
    return restored.drawn.length === Object.keys(restored.chosen).length
      ? 'done'
      : 'active';
  });
  const [index, setIndex] = useState(() =>
    restored ? Object.keys(restored.chosen).length : 0,
  );
  // questionIndex → chosen answer index. Presence = "answered" (locked).
  const [chosen, setChosen] = useState<Record<number, number>>(
    () => restored?.chosen ?? {},
  );
  // Bumped on restart so every attempt draws a fresh subset from the pool.
  const [attempt, setAttempt] = useState(0);
  // Whether quiz_start was sent for this attempt in this visit.
  const startedRef = useRef(false);
  // Expose AntD's colorPrimary as a local CSS var so the stars/score/progress
  // track the button.
  const { token } = theme.useToken();
  const accentStyle = { '--quiz-accent': token.colorPrimary } as CSSProperties;

  // This attempt's questions: a random `questionsPerAttempt`-sized draw from
  // the generated pool (MED-7065). Analytics indices are relative to it.
  // Keyed on the pool's CONTENT, not its identity: the apps re-pull every
  // add-on asset whenever one finishes generating, and that identity-only
  // churn must not redraw the subset under a mid-attempt user's answers.
  const poolKey = useMemo(() => JSON.stringify(pool), [pool]);
  const drawn = useMemo(
    () =>
      attempt === 0 && restored
        ? restored.drawn
        : sampleQuestions(pool, questionsPerAttempt),
    // `poolKey` stands in for `pool` (content, not identity); `attempt`
    // forces the fresh per-attempt draw; `restored` never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [poolKey, questionsPerAttempt, attempt],
  );
  const questions = useMemo(() => drawn.map((d) => d.question), [drawn]);

  const total = questions.length;

  // Count of questions whose chosen answer is correct. Shared by the results
  // screen and the completed-attempt payload so the two can't drift.
  const countCorrect = (picks: Record<number, number>) =>
    questions.reduce((n, q, i) => {
      const picked = picks[i];
      return picked !== undefined && q.answers[picked]?.isCorrect ? n + 1 : n;
    }, 0);

  const restart = () => {
    setChosen({});
    setIndex(0);
    setPhase('start');
    startedRef.current = false;
    onProgressChange?.([]);
    // New attempt → new random subset of the pool.
    setAttempt((n) => n + 1);
  };

  // Lock the picked answer and emit quiz_start (first answer in this visit),
  // quiz_answer, and quiz_attempt once every question is answered.
  const handleSelect = (i: number, answer: QuizAddonAnswer) => {
    const picks = { ...chosen, [index]: i };
    const finished = Object.keys(picks).length === total;
    if (!startedRef.current) {
      startedRef.current = true;
      // Earlier answers were restored from a previous visit.
      analyticEvents?.onStart?.({ resume: Object.keys(chosen).length > 0 });
    }
    setChosen(picks);
    onProgressChange?.(toSavedAnswers(drawn, picks), finished);
    analyticEvents?.onAnswer?.({
      questionIndex: index,
      poolQuestionIndex: drawn[index]?.poolIndex ?? index,
      selectedOptionIndices: [i],
      isCorrect: [answer.isCorrect],
    });
    if (finished) finishAttempt(picks);
  };

  // Build the completed attempt from every chosen answer and emit quiz_attempt.
  const finishAttempt = (picks: Record<number, number>) => {
    const answers: QuizAnswerEvent[] = questions.map((q, i) => {
      const picked = picks[i];
      return {
        questionIndex: i,
        poolQuestionIndex: drawn[i]?.poolIndex ?? i,
        selectedOptionIndices: picked === undefined ? [] : [picked],
        isCorrect:
          picked === undefined ? [] : [Boolean(q.answers[picked]?.isCorrect)],
      };
    });
    const totalCorrect = countCorrect(picks);
    const score = total > 0 ? Math.round((totalCorrect / total) * 100) : 0;
    analyticEvents?.onComplete?.({ answers, score, totalCorrect });
  };

  if (phase === 'start') {
    return (
      <StyledWrapper className={className} style={accentStyle}>
        <StyledCentered>
          <GraduationCapIcon size={40} color={token.colorPrimary} />
          <StyledStartTitle>
            {t('experienceV2.quiz.startTitle')}
          </StyledStartTitle>
          <StyledStartSubtitle>
            {t('experienceV2.quiz.startSubtitle', { total })}
          </StyledStartSubtitle>
          <Button variant="primary" onClick={() => setPhase('active')}>
            {t('experienceV2.quiz.startButton')}
          </Button>
        </StyledCentered>
      </StyledWrapper>
    );
  }

  if (phase === 'done') {
    const correct = countCorrect(chosen);
    return (
      <StyledWrapper className={className} style={accentStyle}>
        <StyledCentered>
          <StyledScore>
            {t('experienceV2.quiz.score', { correct, total })}
          </StyledScore>
          <StyledScoreMessage>
            {t(`experienceV2.quiz.message.${scoreMessageKey(correct, total)}`)}
          </StyledScoreMessage>
          <StyledStars>
            {Array.from({ length: total }, (_, i) => (
              <StyledStar key={i} $earned={i < correct} $index={i}>
                <StarIcon size={26} weight="fill" />
              </StyledStar>
            ))}
          </StyledStars>
          <Button variant="primary" onClick={restart}>
            {t('experienceV2.quiz.tryAgain')}
          </Button>
        </StyledCentered>
      </StyledWrapper>
    );
  }

  const question = questions[index];
  // `index` is always in-bounds, but noUncheckedIndexedAccess (app tsconfig)
  // can't prove it — guard so the lib source typechecks under both configs.
  if (!question) return null;
  const pick = chosen[index];
  const answered = pick !== undefined;
  const isLast = index === total - 1;

  const answerState = (i: number, answer: QuizAddonAnswer): AnswerState => {
    if (!answered) return 'idle';
    if (answer.isCorrect) return i === pick ? 'correct' : 'reveal';
    if (i === pick) return 'wrong';
    return 'idle';
  };

  return (
    <StyledWrapper className={className}>
      <StyledBody>
        <StyledProgress>
          {t('experienceV2.quiz.progress', { current: index + 1, total })}
        </StyledProgress>
        <StyledTrack>
          <StyledFill style={{ width: `${(index / total) * 100}%` }} />
        </StyledTrack>
        <StyledQuestion>{question.question}</StyledQuestion>
        <StyledAnswers role="radiogroup">
          {question.answers.map((answer, i) => {
            const state = answerState(i, answer);
            return (
              <StyledAnswer
                key={`${answer.text}-${i}`}
                type="button"
                role="radio"
                aria-checked={i === pick}
                $state={state}
                disabled={answered}
                onClick={() => handleSelect(i, answer)}
              >
                <span>{answer.text}</span>
                {answerIcon(state)}
              </StyledAnswer>
            );
          })}
        </StyledAnswers>
      </StyledBody>
      <StyledFooter>
        <Button
          variant="primary"
          block
          disabled={!answered}
          onClick={() => (isLast ? setPhase('done') : setIndex(index + 1))}
        >
          {isLast ? t('experienceV2.quiz.finish') : t('experienceV2.quiz.next')}
        </Button>
      </StyledFooter>
    </StyledWrapper>
  );
};
